<template>
  <div :class="$style.loader">
    <div
      v-if="type === 'spinner'"
      :class="[
        $style.spinner,
        {
          [$style.appearanceWhite]: appearance === 'white',
          [$style.appearanceGreyscale]: appearance === 'greyscale',
          [$style.sizeSmall]: size === 'small',
          [$style.sizeLarge]: size === 'large',
          [$style.indeterminate]: indeterminate,
          [$style.inverted]: inverted,
        },
      ]"
    />
    <div
      v-else-if="type === 'dots'"
      :class="[
        $style.dots,
        {
          [$style.sizeSmall]: size === 'small',
          [$style.sizeLarge]: size === 'large',
        },
      ]"
    >
      <span
        :class="[
          $style.dot,
          {
            [$style.appearanceWhite]: appearance === 'white',
            [$style.appearanceGreyscale]: appearance === 'greyscale',
            [$style.inverted]: inverted,
            [$style.sizeSmall]: size === 'small',
            [$style.sizeLarge]: size === 'large',
          },
        ]"
      />
      <span
        :class="[
          $style.dot,
          {
            [$style.appearanceWhite]: appearance === 'white',
            [$style.appearanceGreyscale]: appearance === 'greyscale',
            [$style.inverted]: inverted,
            [$style.sizeSmall]: size === 'small',
            [$style.sizeLarge]: size === 'large',
          },
        ]"
      />
      <span
        :class="[
          $style.dot,
          {
            [$style.appearanceWhite]: appearance === 'white',
            [$style.appearanceGreyscale]: appearance === 'greyscale',
            [$style.inverted]: inverted,
            [$style.sizeSmall]: size === 'small',
            [$style.sizeLarge]: size === 'large',
          },
        ]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    type?: "spinner" | "dots";
    appearance?: "primary" | "white" | "greyscale";
    size?: "large" | "medium" | "small";
    indeterminate?: boolean;
    inverted?: boolean;
  }>(),
  {
    type: "spinner",
    appearance: "primary",
    size: "medium",
    indeterminate: false,
    inverted: false,
  },
);
</script>

<style module lang="scss">
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loader {
  display: inline-flex;
  justify-content: center;
  align-items: center;
}
.spinner {
  border-radius: var(--border-radius-full);
  width: 32px;
  aspect-ratio: 1/1;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: inherit;
    aspect-ratio: 1/1;
    border-radius: var(--border-radius-full);
    border: solid 2px var(--color-interaction-neutral-subtle-normal);
  }
  &::after {
    border: solid 2px var(--color-interaction-default-normal);
    border-top-color: transparent;
    animation: spin 0.8s linear infinite;
  }

  &.appearanceWhite {
    &::before {
      border-color: rgba(var(--color-interaction-inverted-normal), 0.4);
    }
    &::after {
      border-color: var(--color-interaction-inverted-normal);
      border-top-color: transparent;
    }
  }
  &.appearanceGreyscale {
    &::before {
      border-color: var(--color-interaction-neutral-subtle-normal);
    }
    &::after {
      border-color: var(--color-interaction-neutral-normal);
      border-top-color: transparent;
    }
  }

  &.sizeSmall {
    width: 20px;
  }
  &.sizeLarge {
    width: 48px;
  }
  &.indeterminate {
    &::after {
      border-left-color: transparent;
      border-right-color: transparent;
    }
  }
  &.inverted {
    &::before {
      border-color: var(--color-interaction-inverted-normal);
    }
  }
}

.dots {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  height: 32px;

  &.sizeLarge {
    gap: 8px;
  }

  &.sizeSmall {
    gap: 4px;
  }
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-interaction-default-normal);
  animation: dot-bounce 1.2s infinite ease-in-out both;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }

  &.appearanceWhite,
  &.inverted {
    background: var(--color-interaction-inverted-normal);
  }

  &.appearanceGreyscale {
    background: var(--color-interaction-neutral-normal);
  }

  &.sizeLarge {
    width: 10px;
    height: 10px;
  }

  &.sizeSmall {
    width: 6px;
    height: 6px;
  }
}

@keyframes dot-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.7);
  }
  40% {
    transform: scale(1);
  }
}
</style>
